import React, { useState, useEffect } from 'react';
import { Card, Button, Modal, Form, Input, Select, message, Popconfirm, Upload, Tag as AntTag, Space, Spin } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { posters, categories, upload, proxyImageUrl } from '../../api';

const { TextArea } = Input;
const ADMIN_POSTER_THUMBNAIL_OPTIONS = { width: 420, height: 746, mode: 'fill', quality: 82 };

function PosterList() {
  const [data, setData] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewPoster, setPreviewPoster] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();
  const [batchForm] = Form.useForm();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 12, total: 0 });
  const [batchFileList, setBatchFileList] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    fetchData();
  }, [pagination.current, pagination.pageSize, keyword]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        pageSize: pagination.pageSize
      };
      const request = keyword ? posters.search({ ...params, keyword }) : posters.list(params);
      const res = await request;
      setData(res.data.data);
      setPagination(prev => ({ ...prev, total: res.data.pagination.total }));
    } catch (err) {
      message.error('获取数据失败');
    }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const res = await categories.list();
      setCategoryList(res.data.data);
    } catch (err) {
      setCategoryList([]);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue({ tags: '', qr_content: '' });
    setModalVisible(true);
  };

  const handleBatchAdd = () => {
    batchForm.resetFields();
    batchForm.setFieldsValue({ tags: '', qr_content: '' });
    setBatchFileList([]);
    setBatchModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingItem(record);
    form.setFieldsValue({
      ...record,
      tags: record.tag_names || ''
    });
    setModalVisible(true);
  };

  const handlePreview = (record) => {
    setPreviewLoading(true);
    setPreviewPoster(record);
    setPreviewVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await posters.delete(id);
      message.success('删除成功');
      fetchData();
    } catch (err) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const data = {
        ...values,
        tags: values.tags ? values.tags.split(',').map(t => t.trim()).filter(Boolean) : []
      };

      if (editingItem) {
        await posters.update(editingItem.id, data);
        message.success('更新成功');
      } else {
        await posters.create(data);
        message.success('创建成功');
      }

      setModalVisible(false);
      fetchData();
    } catch (err) {
      message.error('操作失败');
    }
  };

  const handleBatchSubmit = async () => {
    try {
      const values = await batchForm.validateFields();
      if (batchFileList.length === 0) {
        message.error('请至少上传一张图片');
        return;
      }

      // 先上传所有图片
      message.loading({ content: '正在上传图片...', key: 'upload' });
      const urls = [];
      for (const file of batchFileList) {
        try {
          const res = await upload.upload(file.originFileObj || file);
          urls.push(res.data.data.url);
        } catch (err) {
          message.error({ content: `上传失败: ${file.name}`, key: 'upload' });
          return;
        }
      }

      message.loading({ content: '正在批量添加海报...', key: 'batch' });
      const data = {
        title: values.title,
        description: values.description,
        image_urls: urls,
        category_id: values.category_id,
        qr_content: values.qr_content,
        tags: values.tags ? values.tags.split(',').map(t => t.trim()).filter(Boolean) : []
      };

      const res = await posters.batchCreate(data);
      message.success({ content: `成功添加 ${res.data.data.count} 张海报`, key: 'batch' });

      setBatchModalVisible(false);
      fetchData();
    } catch (err) {
      message.error('操作失败');
    }
  };

  const handleUpload = async (file) => {
    try {
      const res = await upload.upload(file);
      form.setFieldsValue({ image_url: res.data.data.url });
    } catch (err) {
      message.error('上传失败');
    }
    return false;
  };

  const handleBatchUploadChange = ({ fileList }) => {
    setBatchFileList(fileList);
  };

  const handleSearch = (value) => {
    const nextKeyword = String(value || '').trim();
    setSearchInput(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    setKeyword(nextKeyword);
  };

  // 解析标签
  const parseTags = (tagNames) => {
    if (!tagNames) return [];
    return tagNames.split(',').map(t => t.trim()).filter(Boolean);
  };

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          添加海报
        </Button>
        <Button type="primary" icon={<UploadOutlined />} onClick={handleBatchAdd}>
          批量添加
        </Button>
        <Input.Search
          allowClear
          placeholder="搜索标题或简介"
          value={searchInput}
          onChange={(event) => {
            const nextValue = event.target.value;
            setSearchInput(nextValue);

            if (!nextValue) {
              handleSearch('');
            }
          }}
          onSearch={handleSearch}
          style={{ width: 280 }}
        />
      </Space>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 20 }}>
        {data.map(item => (
          <Card
            key={item.id}
            hoverable
            cover={
              <div
                style={{ aspectRatio: '9/16', overflow: 'hidden', cursor: 'zoom-in' }}
                onClick={() => handlePreview(item)}
              >
                <img 
                  alt={item.title} 
                  src={proxyImageUrl(item.image_url, ADMIN_POSTER_THUMBNAIL_OPTIONS)} 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
            }
            actions={[
              <Button key="edit" type="link" icon={<EditOutlined />} onClick={() => handleEdit(item)}>编辑</Button>,
              <Popconfirm key="delete" title="确定删除？" onConfirm={() => handleDelete(item.id)}>
                <Button danger type="link" icon={<DeleteOutlined />}>删除</Button>
              </Popconfirm>
            ]}
          >
            <Card.Meta 
              title={item.title} 
              description={
                <Space direction="vertical" style={{ width: '100%' }}>
                  <span style={{ color: '#666' }}>{item.category_name}</span>
                  {item.tag_names && (
                    <div>
                      {parseTags(item.tag_names).map((tag, index) => (
                        <AntTag key={index} color="blue" style={{ marginRight: 4 }}>{tag}</AntTag>
                      ))}
                    </div>
                  )}
                </Space>
              }
            />
          </Card>
        ))}
      </div>

      {data.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>暂无海报</div>
      )}

      <div style={{ marginTop: 24, textAlign: 'center' }}>
        <Button.Group>
          <Button 
            disabled={pagination.current <= 1}
            onClick={() => setPagination(prev => ({ ...prev, current: prev.current - 1 }))}
          >
            上一页
          </Button>
          <span style={{ padding: '0 16px', lineHeight: '32px' }}>
            第 {pagination.current} 页 / 共 {Math.ceil(pagination.total / pagination.pageSize) || 1} 页
          </span>
          <Button 
            disabled={pagination.current >= Math.ceil(pagination.total / pagination.pageSize)}
            onClick={() => setPagination(prev => ({ ...prev, current: prev.current + 1 }))}
          >
            下一页
          </Button>
        </Button.Group>
      </div>

      <Modal
        open={previewVisible}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width="auto"
        centered
        className="admin-poster-preview-modal"
      >
        {previewPoster ? (
          <div style={{ maxWidth: 'min(88vw, 540px)' }}>
            <div
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 280,
                minHeight: 360,
                borderRadius: 12,
                background: previewLoading ? 'rgba(15, 23, 42, 0.72)' : 'transparent',
                border: previewLoading ? '1px solid rgba(255, 255, 255, 0.12)' : 'none',
                boxShadow: previewLoading ? '0 30px 80px rgba(15, 23, 42, 0.42)' : 'none'
              }}
            >
              {previewLoading ? <Spin style={{ position: 'absolute' }} /> : null}
              <img
                alt={previewPoster.title}
                src={proxyImageUrl(previewPoster.image_url)}
                onLoad={() => setPreviewLoading(false)}
                onError={() => setPreviewLoading(false)}
                style={{
                  display: 'block',
                  width: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                  borderRadius: 12,
                  opacity: previewLoading ? 0 : 1
                }}
              />
            </div>
            <div style={{ marginTop: 12, fontSize: 16, fontWeight: 600 }}>{previewPoster.title}</div>
          </div>
        ) : null}
      </Modal>

      <Modal
        title={editingItem ? '编辑海报' : '添加海报'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
        maskClosable={false}
        keyboard={false}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="标题" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item name="description" label="简介">
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item
            name="qr_content"
            label="二维码内容"
            tooltip="非必填。填写后，前台编辑页会默认使用这里的内容生成二维码。"
          >
            <TextArea rows={3} placeholder="可填写网址、企微名片、活动说明等二维码内容" />
          </Form.Item>

          <Form.Item name="image_url" label="图片URL" rules={[{ required: true }]}>
            <Input addonBefore={
              <Upload showUploadList={false} beforeUpload={handleUpload} accept="image/*">
                <Button icon={<UploadOutlined />}>上传</Button>
              </Upload>
            } placeholder="https://img2.zhilengwang.cn/haibao/xxx.jpg" />
          </Form.Item>

          <Form.Item name="category_id" label="分类">
            <Select allowClear placeholder="选择分类">
              {categoryList.map(c => (
                <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item 
            name="tags" 
            label="标签" 
            tooltip="多个标签用英文逗号分隔"
          >
            <Input placeholder="例如：节日,促销,春节" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量添加弹窗 */}
      <Modal
        title="批量添加海报"
        open={batchModalVisible}
        onOk={handleBatchSubmit}
        onCancel={() => setBatchModalVisible(false)}
        width={600}
        okText="批量添加"
        maskClosable={false}
        keyboard={false}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <Form form={batchForm} layout="vertical">
          <Form.Item name="title" label="标题" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item name="description" label="简介">
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item
            name="qr_content"
            label="二维码内容"
            tooltip="非必填。批量新增时会把同一份二维码内容写入本次所有海报。"
          >
            <TextArea rows={3} placeholder="可填写网址、企微名片、活动说明等二维码内容" />
          </Form.Item>

          <Form.Item name="image_urls" label="上传图片" rules={[{ required: true, message: '请上传图片' }]}>
            <Upload.Dragger
              listType="picture-card"
              multiple
              accept="image/*"
              beforeUpload={() => false}
              onChange={handleBatchUploadChange}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p>点击或拖拽图片到此区域</p>
              <p style={{ color: '#999', fontSize: 12 }}>支持批量上传，所有图片将使用相同的标题、简介和标签</p>
            </Upload.Dragger>
          </Form.Item>

          <Form.Item name="category_id" label="分类">
            <Select allowClear placeholder="选择分类">
              {categoryList.map(c => (
                <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item 
            name="tags" 
            label="标签" 
            tooltip="多个标签用英文逗号分隔"
          >
            <Input placeholder="例如：节日,促销,春节" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default PosterList;
